/**
 * Pre-bootstrap dev config override.
 *
 * The dev config override (appconfiguration.dev.json) is normally merged in an APP_INITIALIZER (see
 * AppConfigurationService.overrideConfiguration). That is too late for services whose API root is
 * captured eagerly at injector creation: the CoManage data-access feature is imported at the app root
 * and its NgRx effects instantiate the CoManage CrudServices (my/user, my/company, my/companies, ...)
 * before any APP_INITIALIZER runs, so they snapshot the compiled-in base API root and never see the
 * override.
 *
 * Fetching the override here, before bootstrapApplication(), and merging it into the base config (see
 * AppConfigurationService.getCorrectEnvironment) makes the base itself override-correct, so every
 * consumer — eager or lazy — resolves the same dev API roots.
 *
 * Dev only: main.ts gates the call on a non-production / non-nonprod build, and the .dev override is
 * gitignored so it is never present in a deployed build.
 */

let cachedOverride: Record<string, unknown> | null = null;

/**
 * Returns the dev override loaded by loadDevConfigOverride(), or null when none was loaded
 * (production/nonprod builds, or no .dev file present).
 */
export const getDevConfigOverride = (): Record<string, unknown> | null => cachedOverride;

/**
 * Fetches appconfiguration.dev.json (relative to the app's <base href>, so it is correct even on a
 * refreshed deep link) and caches it. A missing/unfetchable file is ignored. Call once, before
 * bootstrapApplication().
 */
export const loadDevConfigOverride = async (): Promise<void> => {
  try {
    const response = await fetch(new URL('appconfiguration/appconfiguration.dev.json', document.baseURI).href, {
      cache: 'no-cache'
    });
    if (response.ok) {
      const json = (await response.json()) as Record<string, unknown>;
      cachedOverride = Object.keys(json).length > 0 ? json : null;
    }
  } catch {
    // A missing/unfetchable .dev override is fine — fall back to the base config.
  }
};
